import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FaCog, FaUser, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

const Settings = () => {
  const { userProfile, fetchUserProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [newUsername, setNewUsername] = useState('');

  const [profileData, setProfileData] = useState({
    displayName: '',
    bio: '',
  });

  useEffect(() => {
    if (userProfile) {
      setProfileData({
        displayName: userProfile.display_name || '',
        bio: userProfile.bio || '',
      });
    }
  }, [userProfile]);

  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 3 || username === userProfile?.username) {
      setUsernameAvailable(null);
      return;
    }

    if (!/^[a-zA-Z0-9]+$/.test(username)) {
      setUsernameAvailable(false);
      return;
    }

    setCheckingUsername(true);
    try {
      await authAPI.getUserByUsername(username);
      setUsernameAvailable(false);
    } catch (error) {
      if (error.response?.status === 404) {
        setUsernameAvailable(true);
      } else {
        setUsernameAvailable(null);
      }
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setNewUsername(value);
    checkUsernameAvailability(value);
  };

  const handleChangeUsername = async (e) => {
    e.preventDefault();

    if (!newUsername || newUsername.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }

    if (!/^[a-zA-Z0-9]+$/.test(newUsername)) {
      toast.error('Username can only contain letters and numbers');
      return;
    }

    if (!usernameAvailable) {
      toast.error('Please choose an available username');
      return;
    }

    if (!window.confirm(
      `Are you sure you want to change your username to "${newUsername}"?\n\n` +
      'This is a ONE-TIME change and cannot be undone!'
    )) {
      return;
    }

    setLoading(true);
    try {
      await authAPI.changeUsername(newUsername);
      await fetchUserProfile();
      toast.success('Username changed successfully!');
      setNewUsername('');
      setUsernameAvailable(null);
    } catch (error) {
      console.error('Change username error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to change username';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authAPI.updateProfile({
        display_name: profileData.displayName,
        bio: profileData.bio,
      });
      await fetchUserProfile();
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!userProfile) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center py-12">Loading...</div>
      </div>
    );
  }

  const hasUsedUsernameChange = userProfile.username_changed;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center mb-8">
        <FaCog className="text-3xl text-boop-primary mr-3" />
        <h1 className="text-3xl font-bold">Account Settings</h1>
      </div>

      <div className="space-y-6">
        {/* Username Change Section */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold mb-2 flex items-center">
                <FaUser className="mr-2 text-boop-secondary" />
                Change Username
              </h2>
              <p className="text-sm text-gray-600">
                Current username: <span className="font-semibold">@{userProfile.username}</span>
              </p>
            </div>
            {hasUsedUsernameChange && (
              <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                Already Used
              </span>
            )}
          </div>

          {hasUsedUsernameChange ? (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FaExclamationTriangle className="text-blue-500 text-xl" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-800">
                    <strong>You have already used your one-time username change.</strong>
                    {userProfile.original_username && (
                      <span className="block mt-1">
                        Your original username was: @{userProfile.original_username}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleChangeUsername}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" htmlFor="newUsername">
                  New Username
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="newUsername"
                    value={newUsername}
                    onChange={handleUsernameChange}
                    placeholder="Enter your new username"
                    className={`w-full px-4 py-3 border rounded-lg pr-10 ${
                      newUsername.length >= 3
                        ? usernameAvailable
                          ? 'border-green-500 focus:ring-green-500'
                          : 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-boop-primary'
                    } focus:outline-none focus:ring-2`}
                    minLength={3}
                    maxLength={50}
                    pattern="[a-zA-Z0-9]+"
                    title="Only letters and numbers allowed"
                  />
                  {checkingUsername && (
                    <div className="absolute right-3 top-3">
                      <div className="animate-spin h-5 w-5 border-2 border-boop-primary border-t-transparent rounded-full"></div>
                    </div>
                  )}
                  {!checkingUsername && newUsername.length >= 3 && usernameAvailable !== null && (
                    <div className="absolute right-3 top-3">
                      {usernameAvailable ? (
                        <FaCheckCircle className="text-green-500 text-xl" />
                      ) : (
                        <span className="text-red-500 text-xl">✗</span>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  3-50 characters, letters and numbers only
                </p>
                {newUsername.length >= 3 && usernameAvailable === false && (
                  <p className="text-xs text-red-500 mt-1">
                    This username is already taken. Try another one!
                  </p>
                )}
                {newUsername.length >= 3 && usernameAvailable === true && (
                  <p className="text-xs text-green-500 mt-1">
                    Great! This username is available
                  </p>
                )}
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4 rounded">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <FaExclamationTriangle className="text-yellow-600 text-xl" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-800">
                      <strong>Warning:</strong> You can only change your username ONCE. This action cannot be undone!
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !usernameAvailable || checkingUsername}
                className="px-6 py-2 bg-boop-primary text-white rounded-lg hover:bg-boop-secondary transition disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {loading ? 'Changing Username...' : 'Change Username'}
              </button>
            </form>
          )}
        </div>

        {/* Profile Information Section */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Profile Information</h2>

          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="displayName">
                Display Name
              </label>
              <input
                type="text"
                id="displayName"
                value={profileData.displayName}
                onChange={(e) => setProfileData({ ...profileData, displayName: e.target.value })}
                placeholder="Your display name"
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
                maxLength={100}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="bio">
                Bio
              </label>
              <textarea
                id="bio"
                value={profileData.bio}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                placeholder="Tell us about yourself..."
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
                rows={4}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {profileData.bio?.length || 0}/500 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-boop-primary text-white rounded-lg hover:bg-boop-secondary transition disabled:opacity-50 font-semibold"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>

        {/* Account Information */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Account Information</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Email:</span>
              <span className="font-semibold">{userProfile.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Level:</span>
              <span className="font-semibold text-boop-primary">{userProfile.level || 'B'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Prompts Created:</span>
              <span className="font-semibold">{userProfile.prompts_count || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Boops Received:</span>
              <span className="font-semibold">{userProfile.total_boops_received || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Member Since:</span>
              <span className="font-semibold">
                {new Date(userProfile.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
